import React, { useCallback, useEffect, useState } from 'react';
import { useLang } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { corList, corStats, corGet, corRegisterPdfUrl } from '../../api';
import { Plus, Search, Printer, Inbox, Send, FolderOpen, AlertTriangle, Paperclip, Archive } from 'lucide-react';
import { L, DIRECTION, STATUS, CATEGORY, ENTITY_TYPE, label, Pill, inputCls } from './correspondence/shared';
import { CorrespondenceForm } from './correspondence/CorrespondenceForm';
import { CorrespondenceDetail } from './correspondence/CorrespondenceDetail';

const AdminCorrespondence = () => {
  const { lang, dir } = useLang();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ search: '', direction: '', status: '', category: '', overdue: false });
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [list, st] = await Promise.all([corList(params), corStats()]);
      setItems(list); setStats(st);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openDetail = async (id) => setSelected(await corGet(id));
  const onChanged = async (doc) => { await load(); if (selected) setSelected(doc || await corGet(selected.id).catch(() => null)); };
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const opts = (map) => Object.keys(map).map((k) => <option key={k} value={k}>{label(map, k, lang)}</option>);

  const cards = stats ? [
    { id: 'total', label: L(lang, 'Total', 'الإجمالي'), v: stats.total, icon: Archive, cls: 'text-[#2563eb]' },
    { id: 'incoming', label: L(lang, 'Incoming', 'وارد'), v: stats.incoming, icon: Inbox, cls: 'text-sky-600' },
    { id: 'outgoing', label: L(lang, 'Outgoing', 'صادر'), v: stats.outgoing, icon: Send, cls: 'text-indigo-600' },
    { id: 'open', label: L(lang, 'Open', 'مفتوحة'), v: stats.open, icon: FolderOpen, cls: 'text-amber-600' },
    { id: 'overdue', label: L(lang, 'Overdue replies', 'ردود متأخرة'), v: stats.overdue, icon: AlertTriangle, cls: stats.overdue ? 'text-rose-600' : 'text-slate-400', onClick: () => setF('overdue', !filters.overdue) },
  ] : [];

  return (
    <div dir={dir} data-testid="admin-correspondence-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#2563eb]">{L(lang, 'Admin · Archive', 'الأدمن · الأرشيف')}</div>
          <h1 className="font-display text-3xl text-[#0a0a0a] mt-1">{L(lang, 'Official Correspondence', 'المخاطبات الرسمية')}</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => window.open(corRegisterPdfUrl({ direction: filters.direction, status: filters.status }), '_blank')} className="ghost-button bg-transparent h-10 px-4 text-sm rounded-md" data-testid="cor-register-pdf-btn"><Printer className="w-4 h-4 me-1" /> {L(lang, 'Register PDF', 'سجل PDF')}</Button>
          <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="gold-button h-10 px-5 text-sm rounded-md" data-testid="cor-new-btn"><Plus className="w-4 h-4 me-1" /> {L(lang, 'New Correspondence', 'مخاطبة جديدة')}</Button>
        </div>
      </div>

      {stats?.overdue > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-700" data-testid="cor-overdue-alert">
          <AlertTriangle className="w-4 h-4" /> {L(lang, `${stats.overdue} correspondence item(s) have an overdue reply.`, `${stats.overdue} مخاطبة تجاوزت تاريخ استحقاق الرد.`)}
          {stats.due_soon > 0 && <span className="text-rose-600/70">· {L(lang, `${stats.due_soon} due within 7 days`, `${stats.due_soon} تستحق خلال 7 أيام`)}</span>}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {cards.map((c) => (
          <button key={c.id} onClick={c.onClick} data-testid={`cor-stat-${c.id}`} className={`glass-card rounded-xl p-4 text-start ${c.onClick ? 'cursor-pointer hover:border-rose-300' : 'cursor-default'} ${c.id === 'overdue' && filters.overdue ? 'ring-2 ring-rose-400' : ''}`}>
            <div className="flex items-center justify-between"><span className="text-[10px] uppercase tracking-widest text-[#111827]/50 font-bold">{c.label}</span><c.icon className={`w-4 h-4 ${c.cls}`} /></div>
            <div className={`font-display text-2xl mt-1 ${c.cls}`}>{c.v}</div>
          </button>
        ))}
      </div>

      <div className="glass-card rounded-2xl p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-2">
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 absolute top-2.5 start-3 text-[#111827]/40" />
          <input value={filters.search} onChange={(e) => setF('search', e.target.value)} placeholder={L(lang, 'Search ref, entity, subject, file…', 'بحث بالمرجع، الجهة، الموضوع، الملف…')} className={`${inputCls} ps-9`} data-testid="cor-search" />
        </div>
        <select value={filters.direction} onChange={(e) => setF('direction', e.target.value)} className={inputCls} data-testid="cor-filter-direction"><option value="">{L(lang, 'All directions', 'كل الأنواع')}</option>{opts(DIRECTION)}</select>
        <select value={filters.status} onChange={(e) => setF('status', e.target.value)} className={inputCls} data-testid="cor-filter-status"><option value="">{L(lang, 'All statuses', 'كل الحالات')}</option>{opts(STATUS)}</select>
        <select value={filters.category} onChange={(e) => setF('category', e.target.value)} className={inputCls} data-testid="cor-filter-category"><option value="">{L(lang, 'All categories', 'كل التصنيفات')}</option>{opts(CATEGORY)}</select>
      </div>

      <div className="glass-card rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="cor-table">
          <thead className="bg-[#f8fafc] text-[10px] uppercase tracking-widest text-[#2563eb]">
            <tr>{[L(lang, 'Ref', 'المرجع'), L(lang, 'Date', 'التاريخ'), L(lang, 'Type', 'النوع'), L(lang, 'Entity', 'الجهة'), L(lang, 'Subject', 'الموضوع'), L(lang, 'Category', 'التصنيف'), L(lang, 'Status', 'الحالة'), L(lang, 'Reply due', 'الاستحقاق'), ''].map((h, i) => <th key={i} className="text-start px-4 py-3 font-bold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} onClick={() => openDetail(it.id)} className="border-t border-[#e5e7eb] hover:bg-[#2563eb]/5 cursor-pointer" data-testid={`cor-row-${it.id}`}>
                <td className="px-4 py-3 font-mono text-xs text-[#2563eb]">{it.id}</td>
                <td className="px-4 py-3 text-[#111827]/70 whitespace-nowrap">{it.date}</td>
                <td className="px-4 py-3"><Pill map={DIRECTION} value={it.direction} lang={lang} /></td>
                <td className="px-4 py-3"><div className="text-[#111827]">{it.entity}</div><div className="text-[10px] text-[#111827]/45">{label(ENTITY_TYPE, it.entity_type, lang)}{it.lead_name ? ` · ${it.lead_name}` : ''}</div></td>
                <td className="px-4 py-3 text-[#111827] max-w-[280px] truncate">{it.subject}</td>
                <td className="px-4 py-3 text-[#111827]/70">{label(CATEGORY, it.category, lang)}</td>
                <td className="px-4 py-3"><Pill map={STATUS} value={it.status} lang={lang} /></td>
                <td className={`px-4 py-3 whitespace-nowrap ${it.overdue ? 'text-rose-600 font-semibold' : 'text-[#111827]/60'}`}>{it.due_at || '—'}{it.overdue && <AlertTriangle className="inline w-3.5 h-3.5 ms-1" />}</td>
                <td className="px-4 py-3 text-[#111827]/50"><span className="inline-flex items-center gap-1 text-xs"><Paperclip className="w-3.5 h-3.5" />{it.attachments_count}</span></td>
              </tr>
            ))}
            {!loading && items.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-[#111827]/45" data-testid="cor-empty">{L(lang, 'No correspondence found', 'لا توجد مخاطبات')}</td></tr>}
          </tbody>
        </table>
      </div>

      <CorrespondenceForm open={formOpen} onClose={() => setFormOpen(false)} lang={lang} initial={editing} onSaved={(doc) => { load(); if (selected && doc?.id === selected.id) setSelected(doc); else if (!editing) setSelected(doc); }} />
      <CorrespondenceDetail item={selected} onClose={() => setSelected(null)} lang={lang} onChanged={onChanged} onEdit={(it) => { setEditing(it); setFormOpen(true); }} />
    </div>
  );
};

export default AdminCorrespondence;
